import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, BookOpenIcon, HeadphonesIcon, PresentationIcon } from 'lucide-react';
type ProductCardProps = {
  title: string;
  image: string;
  benefits: string[];
  ctaText: string;
  reversed?: boolean;
  index: number;
};
export function ProductCard({
  title,
  image,
  benefits,
  ctaText,
  reversed = false,
  index
}: ProductCardProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-20 md:py-32 px-4" data-id="element-85">
      <div className="max-w-6xl mx-auto" data-id="element-86">
        <motion.div initial={{
        opacity: 0,
        y: 60
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        delay: 0.1
      }} className={`flex flex-col ${reversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-16 items-center`} data-id="element-87">
          {/* Image */}
          <motion.div initial={{
          opacity: 0,
          x: reversed ? 50 : -50
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="w-full lg:w-1/2" data-id="element-88">
            <div className="relative group" data-id="element-89">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-90" />
              <img src={image} alt={title} className="relative w-full max-w-md mx-auto rounded-lg shadow-2xl" data-id="element-91" />
            </div>
          </motion.div>

          {/* Content */}
          <div className="w-full lg:w-1/2" data-id="element-92">
            <motion.div initial={{
            opacity: 0
          }} animate={isInView ? {
            opacity: 1
          } : {}} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="flex gap-4 mb-6" data-id="element-93">
              <div className="flex items-center gap-2 text-[#D4AF37]" data-id="element-94">
                <BookOpenIcon className="w-5 h-5" data-id="element-95" />
                <span className="text-sm" data-id="element-96">eBook</span>
              </div>
              <div className="flex items-center gap-2 text-[#D4AF37]" data-id="element-97">
                <HeadphonesIcon className="w-5 h-5" data-id="element-98" />
                <span className="text-sm" data-id="element-99">Audio</span>
              </div>
              <div className="flex items-center gap-2 text-[#D4AF37]" data-id="element-100">
                <PresentationIcon className="w-5 h-5" data-id="element-101" />
                <span className="text-sm" data-id="element-102">Presentación</span>
              </div>
            </motion.div>

            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.4
          }} className="font-display text-2xl md:text-3xl lg:text-4xl font-bold mb-8 leading-tight" data-id="element-103">
              {title}
            </motion.h2>

            <motion.ul initial={{
            opacity: 0
          }} animate={isInView ? {
            opacity: 1
          } : {}} transition={{
            duration: 0.6,
            delay: 0.5
          }} className="space-y-4 mb-10" data-id="element-104">
              {benefits.map((benefit, i) => <motion.li key={i} initial={{
              opacity: 0,
              x: -20
            }} animate={isInView ? {
              opacity: 1,
              x: 0
            } : {}} transition={{
              duration: 0.4,
              delay: 0.6 + i * 0.1
            }} className="flex items-start gap-3" data-id="element-105">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mt-0.5" data-id="element-106">
                    <CheckIcon className="w-4 h-4 text-[#D4AF37]" data-id="element-107" />
                  </span>
                  <span className="text-gray-300 text-lg" data-id="element-108">{benefit}</span>
                </motion.li>)}
            </motion.ul>

            <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.8
          }} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} className="bg-[#D4AF37] text-black font-semibold px-8 py-4 text-lg rounded-sm hover:bg-[#E5C158] transition-colors" data-id="element-109">
              {ctaText}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>;
}